import React, { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiCheckCircle, FiXCircle, FiClock, FiMessageSquare, FiX, FiAlertTriangle,
  FiInbox, FiRefreshCw, FiZap, FiCheck, FiEdit3, FiArchive,
  FiPlusSquare, FiBell, FiCornerUpLeft, FiSend, FiArrowRight,
  FiCornerUpRight, FiTrash2,
} from 'react-icons/fi';
import { OpinionRequestModal, ForwardActionModal } from '../../components/WorkflowActionModals';
import { workflowsAPI, contractsAPI, notificationsAPI } from '../../services/api';
import { tasksReceived } from '../../store/slices/tasksSlice';
import DataGrid from '../../components/DataGrid';
import { toast } from 'react-toastify';
import { isPast } from 'date-fns';
import {
  formatDateTime, formatRelativeTime, formatTimeDistance, formatTimeDistanceCapitalized,
} from '../../utils/dateFormat';
import { formatMoney } from '../../utils/numberFormat';
import { localizeSeeded } from '../FlowManagement/flowUtils';
import { missingPermissionMessage } from '../../utils/permissions';
import { notificationTarget, notificationText } from '../../utils/notificationDisplay';
import { getLocalized } from '../../i18n/getLocalized';
import { contractStatusKey, contractStatusLabel } from '../../utils/contractStatus';
import PageHeader from '../../components/PageHeader/PageHeader';
import './Approvals.scss';

// The Tasks screen (CLM: "Aksiyonlar") — one tabbed workspace for everything
// the user acts on: Pending (approval/review steps assigned to them), Created
// (contracts they created), Drafts (their unsubmitted drafts), and History
// (steps they already decided). Every tab is a DataGrid with search, column
// filters, sorting, grouping, pagination and Excel export.

const TABS = ['pending', 'created', 'drafts', 'history', 'notifications'];

const userName = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '');

// The decision a task offers depends on its step type: an approval step can be
// approved, sent back for changes, or rejected; a review step only gets marked
// reviewed. Keyed by action so the modal renders all four from one place.
// Legacy approvals carry no stepType; they behaved as approvals.
const STEP_STATES = ['review', 'control', 'approval', 'information', 'edit', 'opinion'];
const stepStateKey = (a) => (STEP_STATES.includes(a.stepType) ? a.stepType : 'approval');

const ACTION_ICONS = {
  approve: <FiCheckCircle />,
  reviewed: <FiCheckCircle />,
  requestChanges: <FiCornerUpLeft />,
  reject: <FiXCircle />,
  giveOpinion: <FiMessageSquare />,
  // Control (Kontrol) step decisions: continue or end the process.
  controlPass: <FiCheckCircle />,
  controlFail: <FiXCircle />,
};
const ACTION_BTN_TONE = {
  approve: 'approve',
  reviewed: 'approve',
  requestChanges: 'changes-solid',
  reject: 'reject-solid',
  giveOpinion: 'approve',
  controlPass: 'approve',
  controlFail: 'reject-solid',
};

function Approvals() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // A contract detail's back action returns here with the tab to reopen.
  const [tab, setTab] = useState(
    TABS.includes(location.state?.openTab) ? location.state.openTab : 'pending'
  );

  // Contracts opened from this page carry their navigation context: the
  // detail page's back action returns to this exact tab, and the sidebar
  // keeps My Tasks highlighted instead of switching to Contracts.
  const goToContract = useCallback((path) => {
    navigate(path, { state: { navSource: '/approvals', backTo: { path: '/approvals', tab } } });
  }, [navigate, tab]);

  const [approvals, setApprovals] = useState([]);
  // Contracts returned to this user for edits — rendered in the same list
  // as approvals, since both are work waiting on them.
  const [pendingEdits, setPendingEdits] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(true);
  const [created, setCreated] = useState({ items: [], total: 0, loading: true });
  const [drafts, setDrafts] = useState({ items: [], total: 0, loading: true });
  const [history, setHistory] = useState({ items: [], loading: true });
  // Informational notifications (Bilgilendirmeler): kept 15 days server-side.
  const [notifs, setNotifs] = useState({ items: [], unreadCount: 0, loading: true });
  const [notifFilter, setNotifFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [refreshing, setRefreshing] = useState(false);

  const [actionModal, setActionModal] = useState(null);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);
  // Görüş Al / Yönlendir — per-step abilities of the active action owner.
  const [opinions, setOpinions] = useState([]);
  const [opinionModal, setOpinionModal] = useState(null); // approval to request opinions on
  const [forwardModal, setForwardModal] = useState(null); // approval to forward

  // Pending work is not only approval steps: a contract an approver sent back
  // is waiting on this user too, with no approval record behind it. Both are
  // loaded here and merged into one list of things to act on.
  const loadApprovals = useCallback(async (silent = false) => {
    if (!silent) setLoadingApprovals(true);
    try {
      // One round trip for all three kinds of pending work. The shared tasks
      // slice takes the same list, so the sidebar badge and the dashboard
      // widget reflect a decision made here without a refetch of their own.
      const { data } = await workflowsAPI.pendingTasks();
      setApprovals(data.approvals || []);
      setPendingEdits(data.contracts || []);
      setOpinions(data.opinions || []);
      dispatch(tasksReceived(data));
    } catch (err) {
      toast.error(t('approvals.messages.loadFailed'));
    } finally {
      setLoadingApprovals(false);
    }
  }, [t, dispatch]);

  const loadCreated = useCallback(async () => {
    setCreated((p) => ({ ...p, loading: true }));
    try {
      const { data } = await contractsAPI.list({ createdBy: 'me', limit: 100, sortBy: 'createdAt', sortOrder: 'desc' });
      setCreated({ items: data.contracts || [], total: data.pagination?.total ?? (data.contracts || []).length, loading: false });
    } catch { setCreated((p) => ({ ...p, loading: false })); }
  }, []);

  const loadDrafts = useCallback(async () => {
    setDrafts((p) => ({ ...p, loading: true }));
    try {
      const { data } = await contractsAPI.list({ createdBy: 'me', status: 'draft', limit: 100, sortBy: 'updatedAt', sortOrder: 'desc' });
      setDrafts({ items: data.contracts || [], total: data.pagination?.total ?? (data.contracts || []).length, loading: false });
    } catch { setDrafts((p) => ({ ...p, loading: false })); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistory((p) => ({ ...p, loading: true }));
    try {
      const { data } = await workflowsAPI.approvalHistory();
      setHistory({ items: data.approvals || [], loading: false });
    } catch { setHistory((p) => ({ ...p, loading: false })); }
  }, []);

  const loadNotifs = useCallback(async () => {
    setNotifs((p) => ({ ...p, loading: true }));
    try {
      const { data } = await notificationsAPI.list({ limit: 100 });
      setNotifs({ items: data.notifications || [], unreadCount: data.unreadCount || 0, loading: false });
    } catch { setNotifs((p) => ({ ...p, loading: false })); }
  }, []);

  const loadAll = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    await Promise.all([loadApprovals(silent), loadCreated(), loadDrafts(), loadHistory(), loadNotifs()]);
    setRefreshing(false);
  }, [loadApprovals, loadCreated, loadDrafts, loadHistory, loadNotifs]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Resending a returned contract runs its workflow again from step 1 — the
  // same call the contract page makes, so both routes behave identically.
  const [resending, setResending] = useState(null);
  const handleResend = useCallback(async (row, e) => {
    e?.stopPropagation();
    const contractId = row.contractId?._id;
    const req = row.changeRequest;
    if (!contractId || !req?.workflowId) {
      goToContract(`/contracts/${contractId}`);
      return;
    }
    setResending(contractId);
    try {
      await workflowsAPI.start(contractId, {
        workflowId: req.workflowId,
        ...(req.stepTypeFilter ? { stepType: req.stepTypeFilter } : {}),
      });
      toast.success(t('contractDetail.resent'));
      loadApprovals(true);
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.code === 'VALUE_OUT_OF_RANGE'
        ? t('flows.messages.valueOutOfRange', {
          amount: formatMoney(data.details?.amount),
          currency: data.details?.currency,
          min: data.details?.minValue != null ? formatMoney(data.details.minValue) : '—',
          max: data.details?.maxValue != null ? formatMoney(data.details.maxValue) : '—',
        })
        : data?.code === 'CONTRACT_FIELDS_MISSING'
        ? t('contractDetail.missingFieldsOnResend')
        : data?.error || t('errors.failedToSave'));
    } finally {
      setResending(null);
    }
  }, [goToContract, t, loadApprovals]);

  // The other decision open to the holder of a returned contract: drop it.
  // The request is cleared and the contract goes back to being a draft.
  const handleCancelReturned = useCallback(async (row, e) => {
    e?.stopPropagation();
    const contractId = row.contractId?._id;
    if (!contractId) return;
    if (!window.confirm(t('contractDetail.cancelChangeRequestConfirm'))) return;
    setResending(contractId);
    try {
      await workflowsAPI.cancelChangeRequest(contractId);
      toast.success(t('contractDetail.changeRequestCancelled'));
      loadApprovals(true);
      loadDrafts();
    } catch (err) {
      toast.error(err.response?.data?.error || t('contractDetail.failedToCancelChangeRequest'));
    } finally {
      setResending(null);
    }
  }, [t, loadApprovals, loadDrafts]);

  // ── Approve / reject modal (unchanged behaviour) ──
  const openActionModal = (approval, action, e) => {
    e?.stopPropagation();
    setActionModal({ approval, action });
    setComments('');
  };

  const handleAction = async () => {
    if (!actionModal) return;
    const { approval, action } = actionModal;
    // Both of the negative decisions need a reason — rejecting ends the run,
    // requesting changes tells the initiator what to fix.
    if (!['approve', 'reviewed', 'controlPass'].includes(action) && !comments.trim()) {
      toast.error(action === 'requestChanges'
        ? t('approvals.messages.changeRequestReasonRequired')
        : action === 'giveOpinion'
        ? t('approvals.messages.opinionTextRequired')
        : action === 'controlFail'
        ? t('approvals.messages.controlFailReasonRequired')
        : t('approvals.messages.rejectionReasonRequired'));
      return;
    }
    setProcessing(true);
    const step = localizeSeeded(approval.stepName, i18n.language);
    try {
      if (action === 'giveOpinion') {
        // Not a decision — the requested opinion, in text.
        await workflowsAPI.respondOpinion(approval.opinionId, { opinion: comments.trim() });
        toast.success(t('approvals.messages.opinionSubmitted', { step }));
      } else if (action === 'controlPass') {
        // Uygun Görüldü — the same server transition as an approval: the
        // control gate is passed and the flow moves to the next step.
        await workflowsAPI.approve(approval._id, comments.trim() ? { comments: comments.trim() } : {});
        toast.success(t('approvals.messages.controlPassSuccess', { step }));
      } else if (action === 'controlFail') {
        // Uygun Görülmedi — ends the run; the mandatory reason is stored on
        // the step record and shows in the process history.
        await workflowsAPI.reject(approval._id, { comments: comments.trim() });
        toast.success(t('approvals.messages.controlFailSuccess', { step }));
      } else if (action === 'approve' || action === 'reviewed') {
        // "Reviewed" completes a review step — the same server transition as an
        // approval, but the user is never shown an approve/reject decision.
        await workflowsAPI.approve(approval._id, comments.trim() ? { comments: comments.trim() } : {});
        toast.success(action === 'reviewed'
          ? t('approvals.messages.reviewedSuccess', { step })
          : t('approvals.messages.approveSuccess', { step }));
      } else if (action === 'requestChanges') {
        await workflowsAPI.requestChanges(approval._id, { comments: comments.trim() });
        toast.success(t('approvals.messages.changesRequestedSuccess', { step }));
      } else {
        await workflowsAPI.reject(approval._id, { comments: comments.trim() });
        toast.success(t('approvals.messages.rejectSuccess', { step }));
      }
      setActionModal(null);
      setComments('');
      loadApprovals(true);
      loadHistory();
    } catch (err) {
      const data = err.response?.data;
      toast.error(missingPermissionMessage(data, t, i18n.language)
        || (data?.code === 'SIGNED_DOC_REQUIRED'
          ? t('flows.messages.signedDocRequired')
          : data?.code === 'DRAFT_DOC_REQUIRED_STEP'
          ? t('flows.messages.draftDocRequiredStep')
          : data?.code === 'OPINIONS_PENDING'
          ? t('flows.messages.opinionsPending')
          : data?.code === 'NOT_STEP_ASSIGNEE'
          ? t('flows.messages.notStepAssignee')
          : data?.error || t('approvals.messages.actionFailed', { action })));
    } finally {
      setProcessing(false);
    }
  };

  const getUrgency = (dueAt) => {
    if (!dueAt) return 'normal';
    const due = new Date(dueAt);
    if (isPast(due)) return 'overdue';
    const hoursLeft = (due.getTime() - Date.now()) / (1000 * 60 * 60);
    if (hoursLeft < 12) return 'urgent';
    if (hoursLeft < 24) return 'warning';
    return 'normal';
  };

  // Returned contracts wear the shape of an approval row so one grid renders
  // both: no approval id, no due date, and an 'edit' state of their own.
  // Rows are merged and sorted by the moment the task landed on this user —
  // assignedAt for approval steps, the change-request time for returned
  // contracts — newest first, matching the dashboard widget.
  const pendingRows = useMemo(() => {
    const landedAt = (r) => new Date(r.assignedAt || r.changeRequest?.requestedAt || r.updatedAt || r.createdAt || 0);
    return [
      ...pendingEdits.map((c) => ({
        _id: `edit-${c._id}`,
        isEditTask: true,
        contractId: c,
        changeRequest: c.changeRequest,
        // Both decisions land the contract here; the row says which one did.
        stepName: t(c.approvalState === 'rejected' ? 'approvals.rejectedStep' : 'approvals.editStep'),
        stepType: 'edit',
        dueAt: null,
        updatedAt: c.changeRequest?.requestedAt || null,
      })),
      // Opinion requests waiting on me (Görüş Al) — answered with text only;
      // the step's owner keeps the actual decision.
      ...opinions.map((o) => ({
        _id: `opinion-${o._id}`,
        isOpinionTask: true,
        opinionId: o._id,
        contractId: o.contractId,
        stepName: o.stepName,
        stepType: 'opinion',
        requestedBy: o.requestedBy,
        requestNote: o.requestNote,
        dueAt: null,
        createdAt: o.createdAt,
        updatedAt: o.createdAt,
      })),
      ...approvals,
    ].sort((a, b) => landedAt(b) - landedAt(a));
  }, [approvals, pendingEdits, opinions, t]);

  // ── KPI strip (Pending tab only) ──
  const stats = useMemo(() => {
    const overdue = approvals.filter((a) => a.dueAt && isPast(new Date(a.dueAt))).length;
    const dueToday = approvals.filter((a) => {
      if (!a.dueAt) return false;
      const d = new Date(a.dueAt);
      if (isPast(d)) return false;
      return (d.getTime() - Date.now()) / (1000 * 60 * 60) <= 24;
    }).length;
    const urgent = approvals.filter((a) => ['urgent', 'overdue'].includes(getUrgency(a.dueAt))).length;
    return { pending: approvals.length + pendingEdits.length + opinions.length, overdue, dueToday, urgent };
  }, [approvals, pendingEdits, opinions]);

  // ── Shared formatting ──
  // A contract (or a bare status, for the column filter list) named the way
  // every other screen names it — see utils/contractStatus.
  const formatStatus = useCallback((contractOrStatus) => contractStatusLabel(contractOrStatus, t), [t]);

  const contractTitle = (c) => c?.contractName || c?.counterpartyName || c?.contractNumber || t('approvals.unknownContract');

  const renderContractCell = (c) => (
    <div className="ap-cell-contract">
      <span className="ap-cell-contract__title">
        {contractTitle(c)}
        {c?.versionType === 'addendum' && (
          <span className="badge badge-addendum" style={{ marginLeft: 8 }}>
            {t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })}
          </span>
        )}
      </span>
      {c?.contractNumber && <span className="ap-cell-contract__num">{c.contractNumber}</span>}
    </div>
  );

  // "Overdue by about 24 hours" / "Yaklaşık 24 saat gecikmiş". The duration
  // itself must come from the locale-aware helpers — formatting it with plain
  // date-fns hard-codes English and leaves the sentence half-translated.
  // `timeCap` exists for languages that lead with the duration (TR); English
  // interpolates it mid-sentence and uses `time`.
  const remainingLabel = useCallback((dueAt) => {
    if (!dueAt) return '';
    const d = new Date(dueAt);
    if (!isPast(d)) return formatRelativeTime(d, '');
    return t('approvals.overdueBy', {
      time: formatTimeDistance(d, ''),
      timeCap: formatTimeDistanceCapitalized(d, ''),
      defaultValue: `Overdue by ${formatTimeDistance(d, '')}`,
    });
  }, [t]);

  // ── Column models ──
  // Column order leads with what the user must DO (action + state), then the
  // contract context, and closes with the dates: Action → Status → Priority →
  // Contract → Type → Counterparty → Owner → Step → Due → Time left → Requested.
  const pendingColumns = useMemo(() => [
    {
      key: 'actions',
      label: t('approvals.cols.action', { defaultValue: 'Action' }),
      type: 'text',
      noSort: true,
      noFilter: true,
      noExport: true,
      accessor: () => '',
      render: (a) => (
        a.isEditTask ? (
          // Sent back — for changes, or as not suitable. Edit the contract and
          // send it again (its workflow starts over from the first step), or
          // cancel it, which returns it to being a plain draft.
          <div className="ap-act-group">
            <button
              type="button"
              className="ap-act ap-act--open"
              onClick={(e) => { e.stopPropagation(); goToContract(`/contracts/${a.contractId?._id}/edit`); }}
              title={t('approvals.editContract')}
            >
              <FiEdit3 />
            </button>
            <button
              type="button"
              className="ap-act ap-act--reject"
              disabled={resending === a.contractId?._id}
              onClick={(e) => handleCancelReturned(a, e)}
              title={t('contractDetail.cancelChangeRequest')}
            >
              <FiXCircle />
            </button>
            <button
              type="button"
              className="ap-act ap-act--approve"
              disabled={resending === a.contractId?._id}
              onClick={(e) => handleResend(a, e)}
            >
              <FiSend /> {t('contractDetail.resend')}
            </button>
          </div>
        ) : a.isOpinionTask ? (
          // Görüş Al task: read the contract (row click), then share an
          // opinion — never a decision. The step's owner keeps the approval.
          <div className="ap-act-group">
            <button
              type="button"
              className="ap-act ap-act--review"
              onClick={(e) => openActionModal(a, 'giveOpinion', e)}
            >
              <FiMessageSquare /> {t('approvals.giveOpinion')}
            </button>
          </div>
        ) : a.stepSystemKey === 'signed_contract' ? (
          // The signed-contract step is completed by signing on the contract
          // page (upload/wizard/DocuSign), not by an approve decision here.
          <button
            type="button"
            className="ap-act ap-act--review"
            onClick={(e) => { e.stopPropagation(); goToContract(`/contracts/${a.contractId?._id}`); }}
          >
            <FiEdit3 /> {t('approvals.signAction', { defaultValue: 'İmzala' })}
          </button>
        ) : a.stepType === 'control' ? (
          // Control (Kontrol) step: a continue-or-stop gate. Uygun Görüldü
          // advances the flow; Uygun Görülmedi ends the run (reason required).
          <div className="ap-act-group">
            {a.allowOpinion && (
              <button
                type="button"
                className="ap-act ap-act--opinion"
                onClick={(e) => { e.stopPropagation(); setOpinionModal(a); }}
                title={t('approvals.requestOpinion')}
              >
                <FiMessageSquare />
              </button>
            )}
            {a.allowForward && (
              <button
                type="button"
                className="ap-act ap-act--forward"
                onClick={(e) => { e.stopPropagation(); setForwardModal(a); }}
                title={t('approvals.forwardAction')}
              >
                <FiCornerUpRight />
              </button>
            )}
            <button
              type="button"
              className="ap-act ap-act--reject"
              onClick={(e) => openActionModal(a, 'controlFail', e)}
              title={t('approvals.controlFail')}
            >
              <FiXCircle />
            </button>
            <button
              type="button"
              className="ap-act ap-act--approve"
              onClick={(e) => openActionModal(a, 'controlPass', e)}
            >
              <FiCheckCircle /> {t('approvals.controlPass')}
            </button>
          </div>
        ) : a.stepType === 'review' ? (
          // A review step has exactly one process action — "Reviewed". Opening
          // the contract to make the permitted edits is the row click.
          <div className="ap-act-group">
            {a.allowOpinion && (
              <button
                type="button"
                className="ap-act ap-act--opinion"
                onClick={(e) => { e.stopPropagation(); setOpinionModal(a); }}
                title={t('approvals.requestOpinion')}
              >
                <FiMessageSquare />
              </button>
            )}
            {a.allowForward && (
              <button
                type="button"
                className="ap-act ap-act--forward"
                onClick={(e) => { e.stopPropagation(); setForwardModal(a); }}
                title={t('approvals.forwardAction')}
              >
                <FiCornerUpRight />
              </button>
            )}
            <button
              type="button"
              className="ap-act ap-act--review"
              onClick={(e) => openActionModal(a, 'reviewed', e)}
            >
              <FiCheckCircle /> {t('approvals.reviewed')}
            </button>
          </div>
        ) : (
          <div className="ap-act-group">
            {a.allowOpinion && (
              <button
                type="button"
                className="ap-act ap-act--opinion"
                onClick={(e) => { e.stopPropagation(); setOpinionModal(a); }}
                title={t('approvals.requestOpinion')}
              >
                <FiMessageSquare />
              </button>
            )}
            {a.allowForward && (
              <button
                type="button"
                className="ap-act ap-act--forward"
                onClick={(e) => { e.stopPropagation(); setForwardModal(a); }}
                title={t('approvals.forwardAction')}
              >
                <FiCornerUpRight />
              </button>
            )}
            <button
              type="button"
              className="ap-act ap-act--reject"
              onClick={(e) => openActionModal(a, 'reject', e)}
              title={t('approvals.reject')}
            >
              <FiXCircle />
            </button>
            <button
              type="button"
              className="ap-act ap-act--changes"
              onClick={(e) => openActionModal(a, 'requestChanges', e)}
              title={t('approvals.requestChanges')}
            >
              <FiCornerUpLeft />
            </button>
            <button
              type="button"
              className="ap-act ap-act--approve"
              onClick={(e) => openActionModal(a, 'approve', e)}
            >
              <FiCheckCircle /> {t('approvals.approve')}
            </button>
          </div>
        )
      ),
    },
    {
      key: 'state',
      label: t('contracts.columns.status'),
      type: 'enum',
      groupable: true,
      accessor: (a) => stepStateKey(a),
      optionLabel: (v) => t(`approvals.state.${v}`, { defaultValue: v }),
      render: (a) => (
        <span className={`ap-state ap-state--${stepStateKey(a)}`}>
          {t(`approvals.state.${stepStateKey(a)}`, { defaultValue: a.stepType || 'approval' })}
        </span>
      ),
    },
    {
      key: 'priority',
      label: t('contracts.columns.priority'),
      type: 'enum',
      groupable: true,
      accessor: (a) => a.contractId?.priority || '',
      optionLabel: (v) => t(`contracts.priority.${v}`, { defaultValue: v }),
      render: (a) => {
        const p = a.contractId?.priority;
        return p
          ? <span className={`ap-priority ap-priority--${p}`}><span className="ap-priority__dot" />{t(`contracts.priority.${p}`, { defaultValue: p })}</span>
          : <span className="dg-cell-muted">—</span>;
      },
    },
    {
      key: 'contract',
      label: t('approvals.cols.contract', { defaultValue: 'Contract' }),
      type: 'text',
      accessor: (a) => contractTitle(a.contractId),
      render: (a) => renderContractCell(a.contractId),
    },
    { key: 'type', label: t('contracts.columns.type'), type: 'enum', groupable: true, accessor: (a) => getLocalized(a.contractId?.contractTypeId?.name, i18n.language) || '' },
    { key: 'company', label: t('contracts.columns.company'), type: 'enum', groupable: true, accessor: (a) => a.contractId?.companyId?.name || '' },
    { key: 'counterparty', label: t('contracts.columns.counterparty'), type: 'enum', groupable: true, accessor: (a) => a.contractId?.counterpartyName || '' },
    {
      key: 'owner',
      label: t('contracts.columns.owner'),
      type: 'enum',
      groupable: true,
      accessor: (a) => userName(a.contractId?.responsibleUserId || a.contractId?.createdBy),
    },
    { key: 'step', label: t('approvals.cols.step', { defaultValue: 'Step' }), type: 'enum', groupable: true, accessor: (a) => localizeSeeded(a.stepName || '', i18n.language) },
    { key: 'dueAt', label: t('approvals.cols.dueAt', { defaultValue: 'Due date' }), type: 'date', accessor: (a) => a.dueAt || null },
    {
      key: 'remaining',
      label: t('approvals.cols.remaining', { defaultValue: 'Time left' }),
      type: 'text',
      noSort: true,
      noFilter: true,
      accessor: (a) => remainingLabel(a.dueAt),
      render: (a) => {
        if (!a.dueAt) return <span className="dg-cell-muted">—</span>;
        const u = getUrgency(a.dueAt);
        return <span className={`ap-remaining ap-remaining--${u}`}>{remainingLabel(a.dueAt)}</span>;
      },
    },
    {
      // When the step actually landed on this user's desk — assignedAt for
      // approval steps, the change-request time for returned contracts. Never
      // the contract's own created/updated dates.
      key: 'requestedAt',
      label: t('approvals.cols.requestedAt', { defaultValue: 'Onay İstek Tarihi' }),
      type: 'date',
      accessor: (a) => a.assignedAt || a.changeRequest?.requestedAt || null,
    },
  ], [t, goToContract, remainingLabel, handleResend, resending]);

  const contractColumns = useCallback((withStatus) => [
    { key: 'number', label: t('contracts.columns.number'), type: 'text', accessor: (c) => c.contractNumber || '' },
    {
      key: 'title',
      label: t('contracts.columns.title'),
      type: 'text',
      accessor: (c) => contractTitle(c),
      render: (c) => renderContractCell(c),
    },
    { key: 'type', label: t('contracts.columns.type'), type: 'enum', groupable: true, accessor: (c) => getLocalized(c.contractTypeId?.name, i18n.language) || '' },
    { key: 'company', label: t('contracts.columns.company'), type: 'enum', groupable: true, accessor: (c) => c.companyId?.name || '' },
    { key: 'counterparty', label: t('contracts.columns.counterparty'), type: 'enum', groupable: true, accessor: (c) => c.counterpartyName || '' },
    ...(withStatus ? [{
      key: 'status',
      label: t('contracts.columns.status'),
      type: 'enum',
      groupable: true,
      accessor: (c) => contractStatusKey(c),
      optionLabel: (v) => formatStatus(v),
      render: (c) => <span className={`badge badge-${c.status}`}>{formatStatus(c)}</span>,
    }] : []),
    {
      key: 'value',
      label: t('contracts.columns.value'),
      type: 'number',
      rightAlign: true,
      accessor: (c) => c.contractAmount ?? c.contractValue ?? null,
      render: (c) => {
        const raw = c.contractAmount ?? c.contractValue;
        return raw != null
          ? <span className="ap-amount">{c.currency ? `${c.currency} ` : ''}{formatMoney(raw)}</span>
          : <span className="dg-cell-muted">—</span>;
      },
    },
    { key: 'endDate', label: t('contracts.columns.endDate'), type: 'date', accessor: (c) => c.endDate || null },
    { key: 'updatedAt', label: t('contracts.columns.updatedAt'), type: 'date', accessor: (c) => c.updatedAt || null },
    { key: 'createdAt', label: t('contracts.columns.createdAt'), type: 'date', accessor: (c) => c.createdAt || null },
  ], [t, formatStatus]);

  const createdColumns = useMemo(() => contractColumns(true), [contractColumns]);
  const draftColumns = useMemo(() => contractColumns(false), [contractColumns]);

  const historyColumns = useMemo(() => [
    {
      key: 'contract',
      label: t('approvals.cols.contract', { defaultValue: 'Contract' }),
      type: 'text',
      accessor: (a) => contractTitle(a.contractId),
      render: (a) => renderContractCell(a.contractId),
    },
    { key: 'type', label: t('contracts.columns.type'), type: 'enum', groupable: true, accessor: (a) => getLocalized(a.contractId?.contractTypeId?.name, i18n.language) || '' },
    { key: 'company', label: t('contracts.columns.company'), type: 'enum', groupable: true, accessor: (a) => a.contractId?.companyId?.name || '' },
    { key: 'counterparty', label: t('contracts.columns.counterparty'), type: 'enum', groupable: true, accessor: (a) => a.contractId?.counterpartyName || '' },
    { key: 'step', label: t('approvals.cols.step', { defaultValue: 'Step' }), type: 'enum', groupable: true, accessor: (a) => localizeSeeded(a.stepName || '', i18n.language) },
    {
      key: 'decision',
      label: t('approvals.cols.decision', { defaultValue: 'Decision' }),
      type: 'enum',
      groupable: true,
      accessor: (a) => a.status || '',
      optionLabel: (v) => t(`approvals.decision.${v}`, { defaultValue: v }),
      render: (a) => (
        <span className={`ap-decision ap-decision--${a.status}`}>
          {a.status === 'approved' ? <FiCheckCircle />
            : a.status === 'rejected' ? <FiXCircle />
              : a.status === 'changes_requested' ? <FiCornerUpLeft />
                : a.status === 'deleted' ? <FiTrash2 />
                  : <FiClock />}
          {t(`approvals.decision.${a.status}`, { defaultValue: a.status })}
        </span>
      ),
    },
    { key: 'decidedAt', label: t('approvals.cols.decidedAt', { defaultValue: 'Decided at' }), type: 'date', accessor: (a) => a.decidedAt || a.updatedAt || null },
    { key: 'comments', label: t('approvals.cols.comments', { defaultValue: 'Comments' }), type: 'text', accessor: (a) => a.comments || '' },
  ], [t]);

  const counts = {
    pending: pendingRows.length,
    created: created.total,
    drafts: drafts.total,
    history: history.items.length,
    // The badge shows how many notifications are still unread.
    notifications: notifs.unreadCount,
  };

  const TAB_ICONS = { pending: FiInbox, created: FiPlusSquare, drafts: FiEdit3, history: FiArchive, notifications: FiBell };

  // ── Tab bar: sliding highlight ──
  // The active pill is one element that travels between tabs instead of five
  // backgrounds flicking on and off — the movement itself shows where you came
  // from and where you landed. Measured from the DOM (not computed from index)
  // so it stays exact whatever the label widths are in the active language.
  const tabsRef = useRef(null);
  const tabRefs = useRef({});
  const [pill, setPill] = useState(null);

  // Count badges change the tab widths, so the pill re-measures when they do.
  // Depended on as a string, not the object — `counts` is rebuilt every render,
  // and storing measurements in state from an effect keyed on it would loop.
  const countsKey = TABS.map((k) => counts[k]).join('|');

  useLayoutEffect(() => {
    const measure = () => {
      const el = tabRefs.current[tab];
      if (!el) return;
      const next = {
        left: el.offsetLeft,
        top: el.offsetTop,
        width: el.offsetWidth,
        height: el.offsetHeight,
      };
      // Only write when something actually moved — an unconditional setState
      // here would re-render on every observer callback.
      setPill((prev) => (
        prev
          && prev.left === next.left && prev.top === next.top
          && prev.width === next.width && prev.height === next.height
          ? prev
          : next
      ));
    };
    measure();
    // Labels change width with the language, and the bar wraps on narrow
    // screens — re-measure rather than let the pill drift off its tab.
    const ro = new ResizeObserver(measure);
    if (tabsRef.current) ro.observe(tabsRef.current);
    return () => ro.disconnect();
  }, [tab, i18n.language, countsKey]);

  const openContract = (c) => {
    if (!c?._id) return;
    goToContract(c.status === 'draft' ? `/contracts/${c._id}/edit` : `/contracts/${c._id}`);
  };

  // ── Notifications tab helpers ──
  const filteredNotifs = useMemo(() => {
    if (notifFilter === 'unread') return notifs.items.filter((n) => !n.isRead);
    if (notifFilter === 'read') return notifs.items.filter((n) => n.isRead);
    return notifs.items;
  }, [notifs.items, notifFilter]);

  // Viewing a notification marks it read (optimistic) and jumps to wherever it
  // points — its contract, or a screen like İş Devri for notifications that are
  // about a person rather than a contract.
  const openNotif = (n) => {
    if (!n.isRead) {
      setNotifs((p) => ({
        ...p,
        unreadCount: Math.max(0, p.unreadCount - 1),
        items: p.items.map((x) => (x._id === n._id ? { ...x, isRead: true, readAt: new Date().toISOString() } : x)),
      }));
      notificationsAPI.markRead(n._id).catch(() => {});
    }
    const target = notificationTarget(n);
    if (!target) return;
    // Contracts keep the back-to-this-tab context; other destinations are
    // ordinary navigations.
    if (target.startsWith('/contracts/')) goToContract(target);
    else navigate(target);
  };

  const markAllNotifsRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifs((p) => ({
        ...p,
        unreadCount: 0,
        items: p.items.map((n) => ({ ...n, isRead: true })),
      }));
    } catch { /* refresh will reconcile */ }
  };

  return (
    <div className="ap">
      {/* Header */}
      {/* The description describes the tab you are on, not the screen as a
          whole. Re-keyed per tab so React swaps the node and the enter
          animation replays; the wrapper holds the line's height so the tab bar
          below never jumps while the text changes. */}
      <PageHeader
        title={t('approvals.title')}
        description={(
          <span className="ap__head-sub-wrap">
            <span key={tab} className="ap__head-sub">
              {t(`approvals.tabSubtitles.${tab}`, { defaultValue: t('approvals.subtitle') })}
            </span>
          </span>
        )}
        actions={(
          <button
            className={`ap__icon-btn ${refreshing ? 'is-spinning' : ''}`}
            onClick={() => loadAll(true)}
            title={t('common.refresh')}
          >
            <FiRefreshCw />
          </button>
        )}
      />

      {/* Tabs */}
      <div className="ap__tabs" role="tablist" ref={tabsRef}>
        {pill && (
          <span
            className="ap__tab-pill"
            aria-hidden="true"
            style={{
              transform: `translate(${pill.left}px, ${pill.top}px)`,
              width: pill.width,
              height: pill.height,
            }}
          />
        )}
        {TABS.map((key) => {
          const Icon = TAB_ICONS[key];
          const label = t(`approvals.tabs.${key}`, { defaultValue: key });
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              ref={(el) => { tabRefs.current[key] = el; }}
              className={`ap__tab ${tab === key ? 'ap__tab--active' : ''}`}
              onClick={() => setTab(key)}
            >
              <Icon />
              {/* data-label renders an invisible bold copy underneath, so the
                  tab keeps the same width whether it is active (semibold) or
                  not (medium) — switching tabs must not reflow the bar. */}
              <span className="ap__tab-label" data-label={label}>{label}</span>
              <span className="ap__tab-count">{counts[key]}</span>
            </button>
          );
        })}
      </div>

      {/* The whole panel is re-keyed per tab: switching mounts a fresh node so
          its enter animation runs, which reads as the new tab arriving rather
          than the table silently swapping its rows. */}
      <div key={tab} className="ap__panel">
      {/* Compact KPI strip — only meaningful for the pending queue */}
      {tab === 'pending' && (
        <div className="ap__kpi-strip">
          <div className="ap__kpi-chip">
            <FiInbox />
            <strong>{stats.pending}</strong>
            <span>{t('approvals.kpi.pending')}</span>
          </div>
          <div className="ap__kpi-chip ap__kpi-chip--warning">
            <FiClock />
            <strong>{stats.dueToday}</strong>
            <span>{t('approvals.kpi.dueToday')}</span>
          </div>
          <div className="ap__kpi-chip ap__kpi-chip--danger">
            <FiAlertTriangle />
            <strong>{stats.overdue}</strong>
            <span>{t('approvals.kpi.overdue')}</span>
          </div>
          <div className="ap__kpi-chip ap__kpi-chip--urgent">
            <FiZap />
            <strong>{stats.urgent}</strong>
            <span>{t('approvals.kpi.urgent')}</span>
          </div>
        </div>
      )}

      {/* Active tab grid */}
      {tab === 'pending' && (
        <DataGrid
          columns={pendingColumns}
          rows={pendingRows}
          loading={loadingApprovals}
          storageKey="tasks:cols:pending:v3"
          exportName={t('approvals.tabs.pending', { defaultValue: 'Pending' })}
          searchPlaceholder={t('approvals.searchPlaceholder')}
          onRowClick={(a) => a.contractId?._id && goToContract(`/contracts/${a.contractId._id}`)}
          emptyText={t('approvals.noPendingApprovals')}
        />
      )}
      {tab === 'created' && (
        <DataGrid
          columns={createdColumns}
          rows={created.items}
          loading={created.loading}
          storageKey="tasks:cols:created:v2"
          defaultVisible={['number', 'title', 'type', 'company', 'counterparty', 'status', 'value', 'endDate', 'createdAt']}
          exportName={t('approvals.tabs.created', { defaultValue: 'Created' })}
          searchPlaceholder={t('approvals.searchPlaceholder')}
          onRowClick={openContract}
          emptyText={t('approvals.empty.created')}
        />
      )}
      {tab === 'drafts' && (
        <DataGrid
          columns={draftColumns}
          rows={drafts.items}
          loading={drafts.loading}
          storageKey="tasks:cols:drafts:v2"
          defaultVisible={['number', 'title', 'type', 'company', 'counterparty', 'value', 'updatedAt', 'createdAt']}
          exportName={t('approvals.tabs.drafts', { defaultValue: 'Drafts' })}
          searchPlaceholder={t('approvals.searchPlaceholder')}
          onRowClick={openContract}
          emptyText={t('approvals.empty.drafts')}
        />
      )}
      {tab === 'history' && (
        <DataGrid
          columns={historyColumns}
          rows={history.items}
          loading={history.loading}
          storageKey="tasks:cols:history:v2"
          exportName={t('approvals.tabs.history', { defaultValue: 'History' })}
          searchPlaceholder={t('approvals.searchPlaceholder')}
          onRowClick={(a) => a.contractId?._id && goToContract(`/contracts/${a.contractId._id}`)}
          emptyText={t('approvals.empty.history')}
        />
      )}
      {tab === 'notifications' && (
        <div className="ap-notifs">
          <div className="ap-notifs__toolbar">
            <div className="ap-notifs__filters" role="tablist">
              {['all', 'unread', 'read'].map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`ap-notifs__filter ${notifFilter === f ? 'is-active' : ''}`}
                  onClick={() => setNotifFilter(f)}
                >
                  {t(`approvals.notifs.filter_${f}`, { defaultValue: f })}
                  <span className="ap-notifs__filter-count">
                    {f === 'all' ? notifs.items.length
                      : f === 'unread' ? notifs.unreadCount
                        : notifs.items.length - notifs.unreadCount}
                  </span>
                </button>
              ))}
            </div>
            {notifs.unreadCount > 0 && (
              <button type="button" className="ap-notifs__mark-all" onClick={markAllNotifsRead}>
                <FiCheckCircle /> {t('common.markAllRead')}
              </button>
            )}
          </div>

          {notifs.loading ? (
            <div className="ap-notifs__empty">{t('common.loading')}</div>
          ) : filteredNotifs.length === 0 ? (
            <div className="ap-notifs__empty">
              <FiBell size={26} />
              <p>{t('approvals.notifs.empty', { defaultValue: 'No notifications.' })}</p>
            </div>
          ) : (
            <div className="ap-notifs__list">
              {filteredNotifs.map((n) => {
                const view = notificationText(n, t, i18n.language);
                const clickable = !!notificationTarget(n);
                return (
                  <button
                    key={n._id}
                    type="button"
                    className={[
                      'ap-notif',
                      n.isRead ? 'ap-notif--read' : 'ap-notif--unread',
                      clickable ? 'ap-notif--link' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => openNotif(n)}
                  >
                    <span className="ap-notif__dot" aria-hidden="true" />
                    <span className="ap-notif__body">
                      <span className="ap-notif__title">{view.title}</span>
                      {view.message && <span className="ap-notif__message">{view.message}</span>}
                      {view.detail?.length > 0 && (
                        <span className="ap-notif__tags">
                          {view.detail.slice(0, 4).map((label, i) => (
                            <span key={`${n._id}-tag-${i}`}>{label}</span>
                          ))}
                          {view.detail.length > 4 && <span>+{view.detail.length - 4}</span>}
                        </span>
                      )}
                      {clickable && (
                        <span className="ap-notif__cta">
                          {t('common.open')} <FiArrowRight />
                        </span>
                      )}
                    </span>
                    <span className="ap-notif__meta">
                      <span className="ap-notif__time">{formatDateTime(n.createdAt)}</span>
                      <span className={`ap-notif__state ${n.isRead ? 'is-read' : 'is-unread'}`}>
                        {n.isRead
                          ? t('approvals.notifs.read', { defaultValue: 'Read' })
                          : t('approvals.notifs.unread', { defaultValue: 'Unread' })}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <p className="ap-notifs__retention">
            {t('approvals.notifs.retention', { defaultValue: 'Notifications are removed automatically 15 days after delivery.' })}
          </p>
        </div>
      )}
      </div>

      {/* ══════════════════════════════════════════
          APPROVE / REJECT MODAL
      ══════════════════════════════════════════ */}
      {actionModal && (() => {
      const commentsRequired = !['approve', 'reviewed'].includes(actionModal.action);
      return (
        <div className="ap__modal-overlay" onClick={() => setActionModal(null)}>
          <div className="ap__modal" onClick={e => e.stopPropagation()}>
            <div className={`ap__modal-head ap__modal-head--${actionModal.action}`}>
              <div className="ap__modal-head-icon">
                {ACTION_ICONS[actionModal.action]}
              </div>
              <div className="ap__modal-head-info">
                <h3 className="ap__modal-title">
                  {t(`approvals.actionTitles.${actionModal.action}`)}
                </h3>
                <p className="ap__modal-sub">{actionModal.approval.contractId?.contractName || actionModal.approval.contractId?.counterpartyName || actionModal.approval.contractId?.contractNumber}</p>
              </div>
              <button className="ap__icon-btn" onClick={() => setActionModal(null)}><FiX /></button>
            </div>

            <div className="ap__modal-body">
              <div className="ap__modal-info">
                <div className="ap__modal-info-row">
                  <span className="ap__modal-info-label">{t('approvals.step')}</span>
                  <span className="ap__modal-info-val">{localizeSeeded(actionModal.approval.stepName, i18n.language)}</span>
                </div>
                {actionModal.approval.dueAt && (
                  <div className="ap__modal-info-row">
                    <span className="ap__modal-info-label">{t('common.due')}</span>
                    <span className={`ap__modal-info-val ${isPast(new Date(actionModal.approval.dueAt)) ? 'is-danger' : ''}`}>
                      {formatDateTime(actionModal.approval.dueAt)}
                    </span>
                  </div>
                )}
              </div>

              <div className="ap__field">
                <label className="ap__field-label">
                  <FiMessageSquare />
                  {t(`approvals.actionCommentLabels.${actionModal.action}`)}
                  {commentsRequired && <span className="ap__required">*</span>}
                </label>
                <textarea
                  className="ap__textarea"
                  value={comments}
                  onChange={e => setComments(e.target.value)}
                  rows={4}
                  placeholder={t(`approvals.actionCommentPlaceholders.${actionModal.action}`)}
                />
              </div>
            </div>

            <div className="ap__modal-foot">
              <button className="ap__btn ap__btn--ghost" onClick={() => setActionModal(null)}>
                {t('common.cancel')}
              </button>
              <button
                className={`ap__btn ap__btn--${ACTION_BTN_TONE[actionModal.action]}`}
                onClick={handleAction}
                disabled={processing}
              >
                {processing ? (
                  <><FiRefreshCw className="is-spinning-icon" /> {t('common.processing')}</>
                ) : (
                  <>{ACTION_ICONS[actionModal.action]} {t(`approvals.actionConfirms.${actionModal.action}`)}</>
                )}
              </button>
            </div>
          </div>
        </div>
      );
      })()}

      {/* Görüş Al / Yönlendir — after either action the user is taken to the
          contract's Timeline, where the request/handover just landed. */}
      <OpinionRequestModal
        open={!!opinionModal}
        onClose={() => setOpinionModal(null)}
        approval={opinionModal}
        onDone={() => {
          loadApprovals(true);
          const cid = opinionModal?.contractId?._id;
          if (cid) {
            navigate(`/contracts/${cid}`, {
              state: { openTab: 'timeline', navSource: '/approvals', backTo: { path: '/approvals', tab } },
            });
          }
        }}
      />
      <ForwardActionModal
        open={!!forwardModal}
        onClose={() => setForwardModal(null)}
        approval={forwardModal}
        onDone={() => {
          loadApprovals(true);
          loadHistory();
          const cid = forwardModal?.contractId?._id;
          if (cid) {
            navigate(`/contracts/${cid}`, {
              state: { openTab: 'timeline', navSource: '/approvals', backTo: { path: '/approvals', tab } },
            });
          }
        }}
      />
    </div>
  );
}

export default Approvals;
